import React, { useEffect, useState } from 'react';
import { useApi } from '../services/apiService';
import { formatMoney } from '../utils/format';
import Icon from './Icon';
import './AssetAllocationChart.css';

interface AllocationItem {
  name: string;
  symbol?: string;
  kind: 'asset' | 'account' | 'adjustment';
  value: number;
}

interface AssetAllocation {
  assetClass: string;
  target: number;
  actual: number;
  items: AllocationItem[];
}

const AssetAllocationChart = () => {
  const [data, setData] = useState<AssetAllocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const { getAssetAllocation, syncAssets } = useApi();

  const fetchData = async () => {
    try {
      const response = await getAssetAllocation();
      setData(response);
      setError(null);
    } catch (err) {
      setError('Failed to load asset allocation data');
      console.error('Error fetching asset allocation:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Pulls the latest holdings from Akahu, then reloads the chart.
  const onSyncAssets = async () => {
    setSyncing(true);
    setSyncError(null);
    try {
      await syncAssets();
      await fetchData();
    } catch (err) {
      setSyncError('Failed to sync holdings');
      console.error('Failed to sync assets:', err);
    } finally {
      setSyncing(false);
    }
  };

  const totalActual = data.reduce((sum, row) => sum + Number(row.actual), 0);
  // Leave headroom so the largest bar never touches the edge.
  const scaleMax =
    Math.max(
      1,
      ...data.flatMap((row) => [Number(row.target), Number(row.actual)]),
    ) * 1.08;
  const toWidth = (value: number) =>
    `${Math.max(0, (value / scaleMax) * 100)}%`;

  return (
    <section
      className="card asset-allocation-chart"
      aria-labelledby="allocation-title"
    >
      <div className="section-header">
        <div>
          <h2 id="allocation-title" className="card-title">
            Asset allocation
          </h2>
          <p className="card-subtitle">
            Actual holdings against your target mix. Select a row to see what
            makes it up.
          </p>
        </div>
        <button className="btn" onClick={onSyncAssets} disabled={syncing}>
          <Icon
            name="refresh"
            size={16}
            className={syncing ? 'spin' : undefined}
          />
          {syncing ? 'Syncing…' : 'Sync holdings'}
        </button>
      </div>

      <div className="allocation-legend">
        <span>
          <span className="legend-swatch legend-target" />
          Target
        </span>
        <span>
          <span className="legend-swatch legend-over" />
          On / above target
        </span>
        <span>
          <span className="legend-swatch legend-under" />
          Below target
        </span>
      </div>

      {syncError && <p className="error">{syncError}</p>}
      {loading && <p className="muted">Loading asset allocation...</p>}
      {error && <p className="error">{error}</p>}
      {!loading && !error && data.length === 0 && (
        <p className="muted">No allocation targets set up yet.</p>
      )}

      <ul className="allocation-rows">
        {data.map((row) => {
          const target = Number(row.target);
          const actual = Number(row.actual);
          const diff = actual - target;
          const isOver = diff >= 0;
          const share =
            totalActual > 0 ? Math.round((actual / totalActual) * 100) : 0;
          const isOpen = expanded === row.assetClass;
          const items = row.items ?? [];
          const detailsId = `allocation-details-${row.assetClass.replace(/\W+/g, '-')}`;

          return (
            <li key={row.assetClass} className="allocation-item">
              <button
                type="button"
                className="allocation-row"
                aria-expanded={isOpen}
                aria-controls={detailsId}
                aria-label={`${row.assetClass}: actual ${formatMoney(actual, 0)}, target ${formatMoney(target, 0)}`}
                onClick={() => setExpanded(isOpen ? null : row.assetClass)}
              >
                <div className="allocation-name">
                  <div className="allocation-class">
                    {row.assetClass}
                    <Icon
                      name={isOpen ? 'chevronUp' : 'chevronDown'}
                      size={12}
                    />
                  </div>
                  <div className="allocation-share num">
                    {share}% of portfolio
                  </div>
                </div>
                <div className="allocation-bar" aria-hidden="true">
                  <div
                    className="allocation-bar-target"
                    style={{ width: toWidth(target) }}
                  />
                  <div
                    className={`allocation-bar-actual ${isOver ? 'is-over' : 'is-under'}`}
                    style={{ width: toWidth(actual) }}
                  />
                </div>
                <div className="allocation-values">
                  <div className="allocation-actual num">
                    {formatMoney(actual, 0)}
                  </div>
                  <div
                    className={`allocation-diff num ${isOver ? 'is-over' : 'is-under'}`}
                  >
                    {isOver ? '+' : '−'}
                    {formatMoney(diff, 0)} vs {formatMoney(target, 0)}
                  </div>
                </div>
              </button>
              {isOpen && (
                <div id={detailsId} className="allocation-details">
                  {items.length === 0 ? (
                    <p className="muted">Nothing in this class yet.</p>
                  ) : (
                    <ul className="allocation-holdings">
                      {items.map((item, i) => (
                        <li
                          key={`${item.kind}-${item.name}-${i}`}
                          className={`allocation-holding is-${item.kind}`}
                        >
                          <span className="allocation-holding-name">
                            {item.name}
                            {item.symbol && (
                              <span className="allocation-holding-symbol">
                                {item.symbol}
                              </span>
                            )}
                            {item.kind === 'account' && (
                              <span className="allocation-holding-symbol">
                                Account
                              </span>
                            )}
                          </span>
                          <span className="allocation-holding-value num">
                            {item.value < 0 ? '−' : ''}
                            {formatMoney(item.value, 0)}
                          </span>
                        </li>
                      ))}
                      <li className="allocation-holding is-total">
                        <span className="allocation-holding-name">Total</span>
                        <span className="allocation-holding-value num">
                          {formatMoney(actual, 0)}
                        </span>
                      </li>
                    </ul>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default AssetAllocationChart;
